import React, {
  useMemo,
  useState,
  useEffect,
  useRef,
} from 'react';
import {
  Calendar as CalendarIcon,
  Clock,
  Edit3,
  Activity,
  Tag,
  Save,
  X,
  Plus,
  Trash2,
  UserCheck,
  ShieldCheck,
  Link as LinkIcon,
  Copy,
  Sparkles,
  ArrowUpRight,
} from 'lucide-react';
import axios from 'axios';
import EvolutionAIReviewCard from './ai/EvolutionAIReviewCard';
import { listEvolutionReviews } from '../services/contextualAiApi';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

const STATUS_COLORS = {
  Realizado: '#059669',
  'Não Compareceu': '#F59E0B',
  Planejado: '#3b82f6',
};
const statusColor = (s) => STATUS_COLORS[s] || '#6B7280';

const formatDateBR = (s) => {
  if (!s || !/^\d{4}-\d{2}-\d{2}$/.test(s)) return s || '';
  const [y, m, d] = s.split('-');
  return `${d}/${m}/${y}`;
};

const todayStr = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const nowTimeStr = () => {
  const d = new Date();
  const h = String(d.getHours()).padStart(2, '0');
  const m = String(d.getMinutes()).padStart(2, '0');
  return `${h}:${m}`;
};

const addMonthsToDate = (baseDate, months) => {
  const base =
    baseDate && /^\d{4}-\d{2}-\d{2}$/.test(baseDate)
      ? new Date(`${baseDate}T00:00:00`)
      : new Date();

  base.setMonth(base.getMonth() + months);

  const y = base.getFullYear();
  const m = String(base.getMonth() + 1).padStart(2, '0');
  const d = String(base.getDate()).padStart(2, '0');

  return `${y}-${m}-${d}`;
};

const isAlignerEvolution = (appointment) => {
  if (!appointment) {
    return false;
  }

  if (
    appointment.aligner_event_id ||
    appointment.aligner_treatment_id
  ) {
    return true;
  }

  if (
    appointment.is_aligner_event === true ||
    appointment.origin ===
      'aligner_event'
  ) {
    return true;
  }

  const source = String(
    appointment.source || ''
  )
    .trim()
    .toLowerCase();

  if (
    [
      'aligners',
      'aligner',
      'aligner_event',
      'aligners_event',
    ].includes(source)
  ) {
    return true;
  }

  const procedure = String(
    appointment.procedure || ''
  )
    .trim()
    .toLowerCase();

  return (
    procedure.includes('alinhador') ||
    procedure.includes('alinhadores')
  );
};

export default function EvolutionTab({
  patientData,
  selectedPatient,
  treatments = [],
  toast,
  onDataUpdate,
  logAudit,
  focusedEvolutionId,
  onFocusedEvolutionHandled,
  onOpenAlignerEvent,
}) {
  const [loading, setLoading] = useState(false);
  const [appointments, setAppointments] = useState([]);
  const [odontogramData, setOdontogramData] = useState(null);
  const [aiReviewsByEvolution, setAiReviewsByEvolution] = useState({});
  const [reviewingEvolutionId, setReviewingEvolutionId] = useState(null);

  const [
    highlightedEvolutionId,
    setHighlightedEvolutionId,
  ] = useState(null);

  const evolutionRefs = useRef({});

  // edição de evolução existente
  const [editingId, setEditingId] = useState(null);
  const [evolutionText, setEvolutionText] = useState('');
  const [selectedTreatmentId, setSelectedTreatmentId] = useState('');

  const [professionalName, setProfessionalName] = useState('');
  const [professionalCro, setProfessionalCro] = useState('');
  const [patientAcknowledgementStatus, setPatientAcknowledgementStatus] = useState('pendente');

  // lembrete a partir de evolução existente
  const [createReminder, setCreateReminder] = useState(false);
  const [reminderDate, setReminderDate] = useState('');
  const [reminderTime, setReminderTime] = useState('');
  const [reminderTitle, setReminderTitle] = useState('');

  // nova evolução manual
  const [creatingNew, setCreatingNew] = useState(false);
  const [newEvolutionText, setNewEvolutionText] = useState('');
  const [newDate, setNewDate] = useState(todayStr());
  const [newTime, setNewTime] = useState(nowTimeStr());
  const [newTreatmentId, setNewTreatmentId] = useState('');

  const [newProfessionalName, setNewProfessionalName] = useState('');
  const [newProfessionalCro, setNewProfessionalCro] = useState('');
  const [newPatientAcknowledgementStatus, setNewPatientAcknowledgementStatus] = useState('pendente');

  const [newCreateReminder, setNewCreateReminder] = useState(false);
  const [newReminderDate, setNewReminderDate] = useState('');
  const [newReminderTime, setNewReminderTime] = useState('');
  const [newReminderTitle, setNewReminderTitle] = useState('');

  const patientId = selectedPatient?.id || patientData?.id || '';
  const patientName =
    selectedPatient?.full_name || patientData?.full_name || '';

  useEffect(() => {
    let active = true;
    if (!patientId) {
      setAiReviewsByEvolution({});
      return undefined;
    }

    listEvolutionReviews(patientId)
      .then((items) => {
        if (!active) return;
        const latestBySource = {};
        items.forEach((item) => {
          const sourceId = String(item?.source_id || '');
          if (sourceId && !latestBySource[sourceId]) latestBySource[sourceId] = item;
        });
        setAiReviewsByEvolution(latestBySource);
      })
      .catch(() => {
        // A indisponibilidade da IA não deve impedir a evolução clínica.
      });

    return () => {
      active = false;
    };
  }, [patientId]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  };

  const notifySuccess = (message) => {
    if (toast?.success && typeof toast.success === 'function') {
      toast.success(message);
    } else if (typeof toast === 'function') {
      toast(message);
    } else {
      console.log(message);
    }
  };

  const notifyError = (message) => {
    if (toast?.error && typeof toast.error === 'function') {
      toast.error(message);
    } else if (typeof toast === 'function') {
      toast(message);
    } else {
      console.error(message);
    }
  };

  const handleOpenAlignerEvent = (
    appointment
  ) => {
    const treatmentId =
      appointment?.aligner_treatment_id;

    const eventId =
      appointment?.aligner_event_id;

    if (!treatmentId || !eventId) {
      notifyError(
        'O vínculo técnico com o evento de alinhadores não está disponível neste registro.'
      );
      return;
    }

    if (
      typeof onOpenAlignerEvent !==
      'function'
    ) {
      notifyError(
        'Não foi possível abrir o módulo de alinhadores.'
      );
      return;
    }

    onOpenAlignerEvent({
      treatmentId,
      eventId,
    });
  };

  useEffect(() => {
    if (!patientId) {
      setOdontogramData(null);
      return;
    }

    const fetchOdontogramForEvolution = async () => {
      try {
        const headers = getAuthHeaders();

        const { data } = await axios.get(
          `${API_URL}/odontograma/patient/${patientId}`,
          { headers }
        );

        setOdontogramData(data || null);
      } catch (e) {
        console.error('Erro ao carregar odontograma para evolução clínica:', e);
        setOdontogramData(null);
      }
    };

    fetchOdontogramForEvolution();
  }, [patientId]);

  // Carrega histórico de evoluções a partir dos agendamentos
  useEffect(() => {
    if (!patientId) {
      setAppointments([]);
      return;
    }

    const listRaw = Array.isArray(patientData?.appointments)
      ? patientData.appointments
      : patientData?.appointments?.items || [];

    const onlyThisPatient = (listRaw || []).filter(
      (a) => a?.patient_id === patientId
    );

    const onlyDone = onlyThisPatient.filter((a) =>
      ['Realizado', 'Não Compareceu', 'Registro Inativado'].includes(a.status)
    );

    const odontogramSource = odontogramData || patientData?.odontogram || null;

    const statusLabels = {
      healthy: 'Saudável',
      normal: 'Normal',
      carious: 'Cárie',
      restored: 'Restaurado',
      extracted: 'Extraído/Ausente',
      fractured: 'Fraturado',
      crown: 'Coroa',
      prosthesis: 'Prótese',
    };

    const surfaceLabels = {
      mesial: 'Mesial',
      distal: 'Distal',
      vestibular: 'Vestibular',
      oclusal: 'Oclusal/Incisal',
      lingual: 'Lingual',
      palatino: 'Palatino',
    };

    const valueLabels = {
      normal: 'Normal',
      healthy: 'Saudável',
      carious: 'Cárie',
      restored: 'Restaurado',
      extracted: 'Extraído/Ausente',
      fractured: 'Fraturado',
      crown: 'Coroa',
      prosthesis: 'Prótese',
    };

    let extraAppts = [];

    if (odontogramSource?.teeth?.length) {
      odontogramSource.teeth.forEach((tooth) => {
        const lines = [];

        lines.push(`Registro automático do odontograma - Dente ${tooth.tooth_number}`);

        if (tooth.status && tooth.status !== 'healthy') {
          lines.push(`Status geral: ${statusLabels[tooth.status] || tooth.status}`);
        }

        const surfaces = tooth.surfaces || tooth.faces || {};
        const surfaceLines = Object.entries(surfaces)
          .filter(([, value]) => !!value)
          .map(([surface, value]) => {
            const surfaceLabel = surfaceLabels[surface] || surface;
            const valueLabel = valueLabels[value] || value;
            return `${surfaceLabel}: ${valueLabel}`;
          });

        if (surfaceLines.length > 0) {
          lines.push(`Faces/superfícies: ${surfaceLines.join('; ')}`);
        }

        if (Array.isArray(tooth.procedures) && tooth.procedures.length > 0) {
          lines.push(`Procedimentos realizados: ${tooth.procedures.join('; ')}`);
        }

        if (Array.isArray(tooth.planned_procedures) && tooth.planned_procedures.length > 0) {
          lines.push(`Procedimentos planejados: ${tooth.planned_procedures.join('; ')}`);
        }

        if (tooth.notes) {
          lines.push(`Anotações: ${tooth.notes}`);
        }

        if (lines.length <= 1) return;

        extraAppts.push({
          id: `odontogram_${tooth.tooth_number}`,
          patient_id: patientId,
          status: 'Realizado',
          procedure: `Odontograma - Dente ${tooth.tooth_number}`,
          treatment_id: null,
          evolution_notes: lines.join('\n'),
          appointment_date:
            (tooth.last_updated || odontogramSource.updated_at || '').split('T')[0] ||
            todayStr(),
          start_time: '---',
          _isOdontogram: true,
          _toothNumber: tooth.tooth_number,
        });
      });
    }

    const combined = [...onlyDone, ...extraAppts];

    const sorted = combined.sort((a, b) => {
      const ka = `${a.appointment_date || ''} ${a.start_time || ''}`;
      const kb = `${b.appointment_date || ''} ${b.start_time || ''}`;
      return kb.localeCompare(ka);
    });

    setAppointments(sorted);
  }, [patientId, patientData?.appointments, patientData?.odontogram, odontogramData]);

  useEffect(() => {
    if (!focusedEvolutionId) {
      return;
    }

    const focusedId = String(
      focusedEvolutionId
    );

    const linkedEvolution =
      appointments.find(
        (appointment) =>
          String(appointment?.id) ===
          focusedId
      );

    /*
     * Durante a montagem inicial, os agendamentos ainda
     * podem não ter sido carregados. Nesse caso, o efeito
     * aguardará a próxima atualização de appointments.
     */
    if (!linkedEvolution) {
      return;
    }

    let highlightTimer = null;

    const scrollTimer = window.setTimeout(
      () => {
        const target =
          evolutionRefs.current[focusedId];

        if (!target) {
          return;
        }

        setHighlightedEvolutionId(
          focusedId
        );

        target.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });

        highlightTimer =
          window.setTimeout(() => {
            setHighlightedEvolutionId(
              null
            );
          }, 3500);

        onFocusedEvolutionHandled?.();
      },
      200
    );

    return () => {
      window.clearTimeout(scrollTimer);

      if (highlightTimer) {
        window.clearTimeout(
          highlightTimer
        );
      }
    };
  }, [
    focusedEvolutionId,
    appointments,
    onFocusedEvolutionHandled,
  ]);
  
  const treatmentOptions = useMemo(
    () =>
      treatments.map((t) => ({
        value: t.id,
        label: t.procedure || t.name || t.title || 'Tratamento',
      })),
    [treatments]
  );

  const getTreatmentName = (id) => {
    return (
      treatmentOptions.find((t) => t.value === id)?.label ||
      'Nenhum Tratamento Vinculado'
    );
  };

  const refreshEvolutionStatus = async () => {
    if (!patientId) return;

    try {
      setLoading(true);
      const headers = getAuthHeaders();

      const { data: all } = await axios.get(`${API_URL}/appointments`, {
        headers,
      });

      const list = Array.isArray(all) ? all : all?.items || [];

      const onlyThisPatient = (list || []).filter(
        (a) => a?.patient_id === patientId
      );

      const onlyDone = onlyThisPatient.filter((a) =>
        ['Realizado', 'Não Compareceu', 'Registro Inativado'].includes(a.status)
      );

      const sorted = [...onlyDone].sort((a, b) => {
        const ka = `${a.appointment_date || ''} ${a.start_time || ''}`;
        const kb = `${b.appointment_date || ''} ${b.start_time || ''}`;
        return kb.localeCompare(ka);
      });

      setAppointments(sorted);
      toast?.success?.('Status das evoluções atualizado.');
    } catch (e) {
      console.error('Erro ao atualizar status das evoluções:', e);
      toast?.error?.('Erro ao atualizar status das evoluções.');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyAcknowledgementLink = async (apt) => {
    if (!apt?.id) {
      toast?.error?.('Evolução inválida para gerar link de ciência.');
      return;
    }

    if (!apt.evolution_notes || !apt.evolution_notes.trim()) {
      toast?.error?.('Esta evolução ainda não possui texto clínico para ciência do paciente.');
      return;
    }

    try {
      setLoading(true);
      const headers = getAuthHeaders();

      const { data } = await axios.post(
        `${API_URL}/appointments/${apt.id}/acknowledgement-link`,
        {},
        { headers }
      );

      const publicPath = data?.public_path;
      if (!publicPath) {
        throw new Error('O backend não retornou o link público.');
      }

      const baseUrl = window.location.origin;
      const fullLink = `${baseUrl}${publicPath}`;

      await navigator.clipboard.writeText(fullLink);

      toast?.success?.('Link de ciência copiado para a área de transferência.');
    } catch (e) {
      console.error('Erro ao gerar/copiar link de ciência:', e);
      toast?.error?.(
        e?.response?.data?.detail ||
          e.message ||
          'Erro ao gerar link de ciência.'
      );
    } finally {
      setLoading(false);
    }
  };

  // --------- EDIÇÃO DE EVOLUÇÃO EXISTENTE ---------

  const startEdit = (apt) => {
    setEditingId(apt.id);
    setEvolutionText(apt.evolution_notes || '');
    setSelectedTreatmentId(apt.treatment_id || '');
    setProfessionalName(apt.professional_name || '');
    setProfessionalCro(apt.professional_cro || '');
    setPatientAcknowledgementStatus(apt.patient_acknowledgement_status || 'pendente');
    setCreateReminder(false);
    setReminderDate('');
    setReminderTime('');
    setReminderTitle('');
    setCreatingNew(false); // garante que não fique com dois formulários abertos
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEvolutionText('');
    setSelectedTreatmentId('');
    setProfessionalName('');
    setProfessionalCro('');
    setPatientAcknowledgementStatus('pendente');
    setCreateReminder(false);
    setReminderDate('');
    setReminderTime('');
    setReminderTitle('');
  };

  const handleSaveExisting = async (apt) => {
    if (!evolutionText.trim()) {
      toast?.error('O campo de evolução clínica não pode estar vazio.');
      return;
    }
    setLoading(true);
    try {
      const headers = getAuthHeaders();

      const { data: all } = await axios.get(`${API_URL}/appointments`, {
        headers,
      });
      const list = Array.isArray(all) ? all : all?.items || [];
      const found = list.find((a) => a.id === apt.id);
      if (!found) {
        toast?.error('Agendamento não encontrado para atualização.');
        return;
      }

      const updateData = {
        ...found,
        evolution_notes: evolutionText.trim(),
        treatment_id: selectedTreatmentId || null,
        status: apt.status,
        professional_name: professionalName.trim() || found.professional_name || null,
        professional_cro: professionalCro.trim() || found.professional_cro || null,
        patient_acknowledgement_status: patientAcknowledgementStatus || 'pendente',
      };

      await axios.put(`${API_URL}/appointments/${apt.id}`, updateData, {
        headers: { ...headers, 'X-Clinical-Evolution-Edit': 'true' },
      });

      setAiReviewsByEvolution((current) => {
        const previous = current[String(apt.id)];
        if (!previous) return current;
        return {
          ...current,
          [String(apt.id)]: { ...previous, status: 'stale' },
        };
      });
      setReviewingEvolutionId(String(apt.id));

      if (logAudit) {
        await logAudit('EDITOU_EVOLUCAO_CLINICA', {
          appointment_id: apt.id,
          treatment_id: selectedTreatmentId || null,
          procedure: apt.procedure || null,
        });
      }

      // lembrete opcional a partir da evolução existente
      if (createReminder && reminderDate) {
        try {
          const titleBase =
            reminderTitle.trim() ||
            `Retorno - ${patientName || 'Paciente'}`;
          const payload = {
            title: titleBase,
            description: evolutionText.trim().slice(0, 300) || undefined,
            due_date: reminderDate,
            due_time: reminderTime || undefined,
            type: 'return',
            origin: 'evolution',
            patient_id: patientId || undefined,
            patient_name: patientName || undefined,
          };

          await axios.post(`${API_URL}/reminders`, payload, {
            headers,
          });
        } catch (err) {
          console.error('Erro ao criar lembrete de retorno:', err);
          toast?.error?.(
            'Evolução salva, mas houve erro ao criar o lembrete de retorno.'
          );
        }
      }

      notifySuccess('Evolução clínica atualizada com sucesso.');
      cancelEdit();
      onDataUpdate && onDataUpdate();
    } catch (e) {
      console.error('Erro ao salvar evolução:', e);
      notifyError(
        e?.response?.data?.detail || e.message || 'Erro ao salvar evolução clínica.'
      );
    } finally {
      setLoading(false);
    }
  };

  // --------- INATIVAÇÃO DE EVOLUÇÃO (sem apagar histórico) ---------

  const handleInactiveEvolution = async (apt) => {
    if (apt._isOdontogram) {
      toast?.error?.(
        'Registros originados do odontograma não serão removidos por aqui. Edite o odontograma para ajustar este apontamento.'
      );
      return;
    }

    const reason = window.prompt(
      'Informe o motivo da inativação deste registro clínico. O registro não será apagado do prontuário.'
    );

    if (reason === null) return;

    if (!reason.trim()) {
      toast?.error?.('Informe um motivo para inativar o registro.');
      return;
    }

    const ok = window.confirm(
      'Confirmar inativação deste registro? Ele continuará visível no histórico como "Registro Inativado".'
    );
    if (!ok) return;

    setLoading(true);
    try {
      const headers = getAuthHeaders();

      const { data: all } = await axios.get(`${API_URL}/appointments`, {
        headers,
      });

      const list = Array.isArray(all) ? all : all?.items || [];
      const found = list.find((a) => a.id === apt.id);

      if (!found) {
        toast?.error?.('Registro não encontrado para inativação.');
        return;
      }

      const updateData = {
        ...found,
        status: 'Registro Inativado',
        is_inactive: true,
        inactive_at: new Date().toISOString(),
        inactive_reason: reason.trim(),
      };

      await axios.put(`${API_URL}/appointments/${apt.id}`, updateData, {
        headers,
      });

      if (logAudit) {
        await logAudit('INATIVOU_EVOLUCAO_CLINICA', {
          appointment_id: apt.id,
          procedure: apt.procedure || null,
          appointment_date: apt.appointment_date || null,
          inactive_reason: reason.trim(),
        });
      }

      setAppointments((prev) =>
        prev.map((a) =>
          a.id === apt.id
            ? {
                ...a,
                ...updateData,
              }
            : a
        )
      );

      toast?.success?.('Registro inativado sem apagar o histórico.');
      onDataUpdate && onDataUpdate();
    } catch (e) {
      console.error('Erro ao inativar evolução clínica:', e);
      toast?.error?.(
        e?.response?.data?.detail || e.message || 'Erro ao inativar evolução clínica.'
      );
    } finally {
      setLoading(false);
    }
  };

  // --------- NOVA EVOLUÇÃO MANUAL ---------

  const startCreateNew = () => {
    setCreatingNew(true);
    setEditingId(null);
    setNewEvolutionText('');
    setNewDate(todayStr());
    setNewTime(nowTimeStr());
    setNewTreatmentId('');
    setNewProfessionalName('');
    setNewProfessionalCro('');
    setNewPatientAcknowledgementStatus('pendente');
    setNewCreateReminder(false);
    setNewReminderDate('');
    setNewReminderTime('');
    setNewReminderTitle('');
  };

  const cancelCreateNew = () => {
    setCreatingNew(false);
    setNewEvolutionText('');
    setNewDate(todayStr());
    setNewTime(nowTimeStr());
    setNewTreatmentId('');
    setNewProfessionalName('');
    setNewProfessionalCro('');
    setNewPatientAcknowledgementStatus('pendente');
    setNewCreateReminder(false);
    setNewReminderDate('');
    setNewReminderTime('');
    setNewReminderTitle('');
  };

  const handleSaveNew = async () => {
    if (!patientId || !patientName) {
      notifyError('Paciente não informado para a nova evolução.');
      return;
    }
    if (!newEvolutionText.trim()) {
      notifyError('O campo de evolução clínica não pode estar vazio.');
      return;
    }
    if (!newDate) {
      toast?.error('Informe a data da evolução.');
      return;
    }
    if (!newTime) {
      toast?.error('Informe o horário da evolução.');
      return;
    }

    setLoading(true);
    try {
      const headers = getAuthHeaders();

      const payload = {
        patient_id: patientId,
        patient_name: patientName,
        appointment_date: newDate,
        start_time: newTime,
        end_time: null,
        procedure: 'Evolução Clínica Manual',
        notes: null,
        evolution_notes: newEvolutionText.trim(),
        status: 'Realizado',
        treatment_id: newTreatmentId || null,
        color: '#7c3aed',
        reminders: [],
        is_recurring: false,
        recurring_pattern: null,
        professional_name: newProfessionalName.trim() || null,
        professional_cro: newProfessionalCro.trim() || null,
        patient_acknowledgement_status: newPatientAcknowledgementStatus || 'pendente',
      };

      const createdRes = await axios.post(`${API_URL}/appointments`, payload, { headers });
      const createdEvolutionId = createdRes?.data?.id;
      if (createdEvolutionId) {
        setReviewingEvolutionId(String(createdEvolutionId));
      }

      if (logAudit) {
        await logAudit('CRIOU_EVOLUCAO_CLINICA', {
          appointment_id: createdRes?.data?.id || null,
          appointment_date: newDate,
          start_time: newTime,
          treatment_id: newTreatmentId || null,
          procedure: 'Evolução Clínica Manual',
        });
      }

      // lembrete opcional a partir da nova evolução manual
      if (newCreateReminder && newReminderDate) {
        try {
          const titleBase =
            newReminderTitle.trim() ||
            `Retorno - ${patientName || 'Paciente'}`;
          const payloadReminder = {
            title: titleBase,
            description: newEvolutionText.trim().slice(0, 300) || undefined,
            due_date: newReminderDate,
            due_time: newReminderTime || undefined,
            type: 'return',
            origin: 'evolution_manual',
            patient_id: patientId || undefined,
            patient_name: patientName || undefined,
          };

          await axios.post(`${API_URL}/reminders`, payloadReminder, {
            headers,
          });
        } catch (err) {
          console.error('Erro ao criar lembrete de retorno:', err);
          toast?.error?.(
            'Evolução criada, mas houve erro ao criar o lembrete de retorno.'
          );
        }
      }

      // Recarrega a lista de evoluções localmente, para aparecer na hora
      try {
        const { data: all } = await axios.get(`${API_URL}/appointments`, {
          headers,
        });
        const list = Array.isArray(all) ? all : all?.items || [];
        const onlyThisPatient = (list || []).filter(
          (a) => a?.patient_id === patientId
        );
        const onlyDone = onlyThisPatient.filter((a) =>
          ['Realizado', 'Não Compareceu', 'Registro Inativado'].includes(a.status)
        );
        const sorted = [...onlyDone].sort((a, b) => {
          const ka = `${a.appointment_date || ''} ${a.start_time || ''}`;
          const kb = `${b.appointment_date || ''} ${b.start_time || ''}`;
          return kb.localeCompare(ka);
        });
        setAppointments(sorted);
      } catch (reloadErr) {
        console.error(
          'Erro ao recarregar evoluções após nova evolução:',
          reloadErr
        );
      }

      notifySuccess('Nova evolução clínica registrada com sucesso.');
      onDataUpdate && onDataUpdate();
    } catch (e) {
      console.error('Erro ao criar nova evolução:', e);
      notifyError(
        e?.response?.data?.detail ||
          e?.message ||
          'Erro ao criar nova evolução clínica.'
      );
    } finally {
      cancelCreateNew();
      setLoading(false);
    }
  };

  // permite que outros componentes (ex: agendamentos) forcem edição de um ID
  useEffect(() => {
    const handler = (e) => {
      if (e.detail) {
        const aptId = e.detail;
        const target = appointments.find((a) => a.id === aptId);
        if (target) {
          startEdit(target);
        }
      }
    };
    window.addEventListener('evolution:startEdit', handler);
    return () => window.removeEventListener('evolution:startEdit', handler);
  }, [appointments]);

  if (!patientId) {
    return (
      <div className="text-sm text-red-600 bg-red-50 border border-red-100 p-3 rounded">
        Paciente não informado.
      </div>
    );
  }

  return (
    <div>
      {/* Título + botão Nova Evolução */}
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-900">
          Evolução Clínica ({appointments.length})
        </h3>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={refreshEvolutionStatus}
            className="inline-flex items-center px-3 py-1.5 rounded-lg bg-gray-100 text-gray-700 text-sm hover:bg-gray-200 disabled:opacity-60"
            disabled={loading}
            title="Atualizar status de ciência do paciente"
          >
            <ShieldCheck className="w-4 h-4 mr-1" />
            Atualizar status
          </button>

          <button
            type="button"
            onClick={startCreateNew}
            className="inline-flex items-center px-3 py-1.5 rounded-lg bg-purple-600 text-white text-sm hover:bg-purple-700 disabled:opacity-60"
            disabled={loading}
          >
            <Plus className="w-4 h-4 mr-1" />
            Nova Evolução
          </button>
        </div>
      </div>

      {/* Formulário de Nova Evolução Manual */}
      {creatingNew && (
        <div className="mb-6 bg-purple-50 border border-purple-200 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-purple-900 mb-3">
            Nova evolução clínica manual
          </h4>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Data da evolução
              </label>
              <input
                type="date"
                className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                value={newDate}
                onChange={(e) => setNewDate(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Horário
              </label>
              <input
                type="time"
                className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                value={newTime}
                onChange={(e) => setNewTime(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Tratamento vinculado (opcional)
              </label>
              <select
                className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                value={newTreatmentId}
                onChange={(e) => setNewTreatmentId(e.target.value)}
              >
                <option value="">Nenhum</option>
                {treatmentOptions.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="mb-3">
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Evolução clínica
            </label>
            <textarea
              className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              rows={3}
              value={newEvolutionText}
              onChange={(e) => setNewEvolutionText(e.target.value)}
              placeholder="Descreva o que foi realizado ou observado nesta evolução..."
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3 mb-3">
            <div>
              <label className="block text-xs text-gray-600 mb-1">Profissional responsável</label>
              <input
                type="text"
                className="w-full border rounded-lg px-3 py-2 text-sm"
                value={newProfessionalName}
                onChange={(e) => setNewProfessionalName(e.target.value)}
                placeholder="Nome do cirurgião-dentista"
              />
            </div>

            <div>
              <label className="block text-xs text-gray-600 mb-1">CRO/UF</label>
              <input
                type="text"
                className="w-full border rounded-lg px-3 py-2 text-sm"
                value={newProfessionalCro}
                onChange={(e) => setNewProfessionalCro(e.target.value)}
                placeholder="Ex.: CRO-PE 00000"
              />
            </div>

            <div>
              <label className="block text-xs text-gray-600 mb-1">Ciência do paciente</label>
              <select
                className="w-full border rounded-lg px-3 py-2 text-sm"
                value={newPatientAcknowledgementStatus}
                onChange={(e) => setNewPatientAcknowledgementStatus(e.target.value)}
              >
                <option value="pendente">Pendente</option>
                <option value="ciente">Paciente ciente</option>
                <option value="assinado">Assinado</option>
                <option value="recusou_assinar">Recusou assinar</option>
              </select>
            </div>
          </div>

          <div className="mt-4 rounded-xl border-2 border-amber-300 bg-amber-50 p-4 shadow-sm space-y-3">
            <label className="inline-flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                className="mt-1 h-4 w-4 rounded border-amber-400 text-amber-600"
                checked={newCreateReminder}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setNewCreateReminder(checked);
                  if (checked && !newReminderDate) {
                    setNewReminderDate(newDate);
                  }
                }}
              />
              <span>
                <span className="block text-sm font-semibold text-amber-900">
                  📅 Programar retorno deste paciente - Lembrete
                </span>
                <span className="mt-1 block text-xs text-amber-800">
                  Agende agora o próximo retorno para não esquecer o acompanhamento do tratamento.
                </span>
              </span>
            </label>
            {newCreateReminder && (
              <div className="rounded-lg border border-purple-200 bg-white p-3 space-y-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[11px] font-medium text-purple-900">
                    Retorno rápido:
                  </span>

                  <button
                    type="button"
                    onClick={() => setNewReminderDate(addMonthsToDate(newDate, 1))}
                    className="rounded-full bg-amber-200 px-3 py-1 text-[11px] font-semibold text-amber-900 hover:bg-amber-300"
                  >
                    1 mês
                  </button>

                  <button
                    type="button"
                    onClick={() => setNewReminderDate(addMonthsToDate(newDate, 3))}
                    className="rounded-full bg-amber-200 px-3 py-1 text-[11px] font-semibold text-amber-900 hover:bg-amber-300"
                  >
                    3 meses
                  </button>

                  <button
                    type="button"
                    onClick={() => setNewReminderDate(addMonthsToDate(newDate, 6))}
                    className="rounded-full bg-amber-200 px-3 py-1 text-[11px] font-semibold text-amber-900 hover:bg-amber-300"
                  >
                    6 meses
                  </button>

                  <span className="text-[11px] font-medium text-amber-800">
                    ou escolha uma data personalizada abaixo
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <div>
                  <span className="block text-[11px] text-gray-600 mb-1">
                    Data do lembrete
                  </span>
                  <input
                    type="date"
                    className="w-full border rounded-lg px-2 py-1.5 text-xs"
                    value={newReminderDate}
                    onChange={(e) => setNewReminderDate(e.target.value)}
                  />
                </div>
                <div>
                  <span className="block text-[11px] text-gray-600 mb-1">
                    Horário (opcional)
                  </span>
                  <input
                    type="time"
                    className="w-full border rounded-lg px-2 py-1.5 text-xs"
                    value={newReminderTime}
                    onChange={(e) => setNewReminderTime(e.target.value)}
                  />
                </div>
                <div className="md:col-span-3">
                  <span className="block text-[11px] text-gray-600 mb-1">
                    Título do lembrete (opcional)
                  </span>
                  <input
                    type="text"
                    className="w-full border rounded-lg px-2 py-1.5 text-xs"
                    value={newReminderTitle}
                    onChange={(e) => setNewReminderTitle(e.target.value)}
                    placeholder="Retorno deste atendimento / procedimento"
                  />
                </div>
                </div>
              </div>
            )}
          </div>

          <div className="flex gap-2 mt-4">
            <button
              type="button"
              onClick={handleSaveNew}
              className="inline-flex items-center px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-sm hover:bg-emerald-700 disabled:opacity-60"
              disabled={loading}
            >
              <Save className="w-4 h-4 mr-1" />
              {loading ? 'Salvando...' : 'Salvar evolução'}
            </button>
            <button
              type="button"
              onClick={cancelCreateNew}
              className="inline-flex items-center px-3 py-1.5 rounded-lg bg-gray-200 text-gray-700 text-sm hover:bg-gray-300"
            >
              <X className="w-4 h-4 mr-1" />
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* Lista de evoluções existentes */}
      {appointments.length > 0 ? (
        <div className="space-y-4">
          {appointments.map((apt) => {
            const aptId = String(
              apt.id
            );

            const isHighlighted =
              String(
                highlightedEvolutionId || ''
              ) === aptId;

            const fromAligners =
              isAlignerEvolution(apt);

            return (
              <div
                key={apt.id}
                ref={(element) => {
                  if (element) {
                    evolutionRefs.current[
                      aptId
                    ] = element;
                  } else {
                    delete evolutionRefs.current[
                      aptId
                    ];
                  }
                }}
                className={`
                  rounded-lg
                  border
                  p-4
                  transition-all
                  duration-500
                  ${
                    isHighlighted
                      ? 'border-purple-500 bg-purple-50 shadow-lg ring-4 ring-purple-200'
                      : 'border-gray-200 bg-gray-50'
                  }
                `}
              >
              <div className="flex justify-between items-start">
                <div className="flex items-center gap-2">
                  <span
                    className="inline-block w-2.5 h-2.5 rounded-full"
                    style={{ backgroundColor: statusColor(apt.status) }}
                  />
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <h4 className="font-semibold text-gray-900">
                        {apt.procedure ||
                          'Consulta'}
                      </h4>

                      {fromAligners && (
                        <span
                          className="
                            inline-flex
                            items-center
                            gap-1
                            rounded-full
                            border
                            border-purple-200
                            bg-purple-100
                            px-2
                            py-0.5
                            text-[10px]
                            font-bold
                            uppercase
                            tracking-wide
                            text-purple-700
                          "
                          title="Registro originado do módulo de Alinhadores"
                        >
                          <Sparkles
                            className="h-3 w-3"
                          />
                          Alinhadores
                        </span>
                      )}

                      <span className="flex items-center gap-1 text-xs text-gray-500">
                        <Tag className="h-3 w-3" />
                        {getTreatmentName(
                          apt.treatment_id
                        )}
                      </span>
                    </div>
                    <div className="flex items-center text-sm text-gray-600 mt-1">
                      <CalendarIcon className="w-4 h-4 mr-2" />
                      <span>{formatDateBR(apt.appointment_date)}</span>
                      <Clock className="w-4 h-4 ml-4 mr-2" />
                      <span>
                        {apt.start_time || '—'}
                        {apt.end_time ? ` às ${apt.end_time}` : ''}
                      </span>
                    </div>
                  </div>
                </div>
                <span
                  className="px-2 py-1 rounded-full text-xs font-medium"
                  style={{
                    backgroundColor: statusColor(apt.status) + '20',
                    color: statusColor(apt.status),
                  }}
                >
                  {apt.status}
                </span>
              </div>

              {fromAligners && (
                <div className="mt-3 flex flex-col gap-3 rounded-lg border border-purple-200 bg-purple-50 px-3 py-2 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-start gap-2">
                    <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-purple-600" />

                    <div>
                      <p className="text-xs font-semibold text-purple-900">
                        Registro integrado ao acompanhamento
                        de alinhadores
                      </p>

                      <p className="mt-0.5 text-[11px] leading-relaxed text-purple-700">
                        Esta evolução foi criada a partir de
                        um evento registrado no módulo de
                        Alinhadores.
                      </p>
                    </div>
                  </div>

                  {apt.aligner_event_id &&
                    apt.aligner_treatment_id && (
                      <button
                        type="button"
                        onClick={() =>
                          handleOpenAlignerEvent(
                            apt
                          )
                        }
                        className="
                          inline-flex
                          shrink-0
                          items-center
                          justify-center
                          gap-1.5
                          self-start
                          rounded-lg
                          border
                          border-purple-300
                          bg-white
                          px-3
                          py-1.5
                          text-xs
                          font-semibold
                          text-purple-700
                          shadow-sm
                          transition
                          hover:border-purple-400
                          hover:bg-purple-100
                          hover:text-purple-900
                          focus:outline-none
                          focus:ring-2
                          focus:ring-purple-400
                          focus:ring-offset-1
                          sm:self-center
                        "
                        title="Abrir o evento que originou esta evolução"
                      >
                        Abrir evento de alinhadores

                        <ArrowUpRight
                          className="h-3.5 w-3.5"
                        />
                      </button>
                    )}
                </div>
              )}

              <div className="mt-3">
                {editingId === apt.id ? (
                  <div className="space-y-2">
                    <textarea
                      className="w-full border rounded-lg px-3 py-2 text-sm"
                      rows={3}
                      value={evolutionText}
                      onChange={(e) => setEvolutionText(e.target.value)}
                      placeholder="Descreva o que foi realizado neste atendimento..."
                    />
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Profissional responsável</label>
                        <input
                          type="text"
                          className="w-full border rounded-lg px-3 py-2 text-sm"
                          value={professionalName}
                          onChange={(e) => setProfessionalName(e.target.value)}
                          placeholder="Nome do cirurgião-dentista"
                        />
                      </div>

                      <div>
                        <label className="block text-xs text-gray-600 mb-1">CRO/UF</label>
                        <input
                          type="text"
                          className="w-full border rounded-lg px-3 py-2 text-sm"
                          value={professionalCro}
                          onChange={(e) => setProfessionalCro(e.target.value)}
                          placeholder="Ex.: CRO-PE 00000"
                        />
                      </div>

                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Ciência do paciente</label>
                        <select
                          className="w-full border rounded-lg px-3 py-2 text-sm"
                          value={patientAcknowledgementStatus}
                          onChange={(e) => setPatientAcknowledgementStatus(e.target.value)}
                        >
                          <option value="pendente">Pendente</option>
                          <option value="ciente">Paciente ciente</option>
                          <option value="assinado">Assinado</option>
                          <option value="recusou_assinar">Recusou assinar</option>
                        </select>
                      </div>
                    </div>                    
                    <div className="flex flex-col gap-3">
                      <div className="flex items-center gap-2 flex-wrap">
                        <select
                          className="p-2 border border-gray-300 rounded-lg text-sm"
                          value={selectedTreatmentId}
                          onChange={(e) =>
                            setSelectedTreatmentId(e.target.value)
                          }
                        >
                          <option value="">
                            Vincular a um Tratamento (opcional)
                          </option>
                          {treatmentOptions.map((t) => (
                            <option key={t.value} value={t.value}>
                              {t.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="mt-4 rounded-xl border-2 border-amber-300 bg-amber-50 p-4 shadow-sm space-y-3">
                        <label className="inline-flex items-start gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            className="mt-1 h-4 w-4 rounded border-amber-400 text-amber-600"
                            checked={createReminder}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setCreateReminder(checked);
                              if (checked && !reminderDate) {
                                setReminderDate(todayStr());
                              }
                            }}
                          />
                          <span>
                            <span className="block text-sm font-semibold text-amber-900">
                              📅 Programar retorno deste paciente - Lembrete
                            </span>
                            <span className="mt-1 block text-xs text-amber-800">
                              Agende agora o próximo retorno para não esquecer o acompanhamento do tratamento.
                            </span>
                          </span>
                        </label>
                        {createReminder && (
                          <div className="rounded-lg border border-gray-200 bg-white p-3 space-y-3">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="text-[11px] font-medium text-gray-800">
                                Retorno rápido:
                              </span>

                              <button
                                type="button"
                                onClick={() => setReminderDate(addMonthsToDate(apt.appointment_date, 1))}
                                className="rounded-full bg-amber-200 px-3 py-1 text-[11px] font-semibold text-amber-900 hover:bg-amber-300"
                              >
                                1 mês
                              </button>

                              <button
                                type="button"
                                onClick={() => setReminderDate(addMonthsToDate(apt.appointment_date, 3))}
                                className="rounded-full bg-amber-200 px-3 py-1 text-[11px] font-semibold text-amber-900 hover:bg-amber-300"
                              >
                                3 meses
                              </button>

                              <button
                                type="button"
                                onClick={() => setReminderDate(addMonthsToDate(apt.appointment_date, 6))}
                                className="rounded-full bg-amber-200 px-3 py-1 text-[11px] font-semibold text-amber-900 hover:bg-amber-300"
                              >
                                6 meses
                              </button>

                              <span className="text-[11px] font-medium text-amber-800">
                                ou escolha uma data personalizada abaixo
                              </span>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                            <div>
                              <span className="block text-[11px] text-gray-600 mb-1">
                                Data do lembrete
                              </span>
                              <input
                                type="date"
                                className="w-full border rounded-lg px-2 py-1.5 text-xs"
                                value={reminderDate}
                                onChange={(e) =>
                                  setReminderDate(e.target.value)
                                }
                              />
                            </div>
                            <div>
                              <span className="block text-[11px] text-gray-600 mb-1">
                                Horário (opcional)
                              </span>
                              <input
                                type="time"
                                className="w-full border rounded-lg px-2 py-1.5 text-xs"
                                value={reminderTime}
                                onChange={(e) =>
                                  setReminderTime(e.target.value)
                                }
                              />
                            </div>
                            <div className="md:col-span-3">
                              <span className="block text-[11px] text-gray-600 mb-1">
                                Título do lembrete (opcional)
                              </span>
                              <input
                                type="text"
                                className="w-full border rounded-lg px-2 py-1.5 text-xs"
                                value={reminderTitle}
                                onChange={(e) =>
                                  setReminderTitle(e.target.value)
                                }
                                placeholder={`Retorno - ${getTreatmentName(
                                  apt.treatment_id
                                )}`}
                              />
                            </div>
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="flex gap-2">
                        <button
                          onClick={() => handleSaveExisting(apt)}
                          className="px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-sm flex items-center hover:bg-emerald-700 disabled:opacity-60"
                          disabled={loading}
                        >
                          <Save className="w-4 h-4 mr-1" />
                          {loading ? 'Salvando...' : 'Salvar'}
                        </button>
                        <button
                          onClick={cancelEdit}
                          className="px-3 py-1.5 bg-gray-200 text-gray-700 rounded-lg text-sm flex items-center hover:bg-gray-300"
                        >
                          <X className="w-4 h-4 mr-1" />
                          Cancelar
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-white border rounded-lg p-3 text-sm text-gray-800 whitespace-pre-wrap">
                    {apt.evolution_notes && apt.evolution_notes.trim()
                      ? apt.evolution_notes
                      : 'Nenhuma evolução clínica registrada.'}

                    <div className="mt-3 pt-3 border-t text-xs text-gray-600 space-y-1 whitespace-normal">
                      {(apt.professional_name || apt.professional_cro) && (
                        <div className="flex items-center gap-2">
                          <UserCheck className="w-4 h-4" />
                          <span>
                            Profissional: {apt.professional_name || '—'}
                            {apt.professional_cro ? ` • ${apt.professional_cro}` : ''}
                          </span>
                        </div>
                      )}

                      <div className="flex items-center gap-2">
                        <ShieldCheck className="w-4 h-4" />
                        <span>
                          Ciência do paciente:{' '}
                          {apt.patient_acknowledgement_status === 'ciente'
                            ? 'Ciência confirmada por link'
                            : apt.patient_acknowledgement_status === 'assinado'
                            ? 'Assinado'
                            : apt.patient_acknowledgement_status === 'recusou_assinar'
                            ? 'Recusou assinar'
                            : 'Pendente'}
                        </span>
                      </div>

                      {apt.is_inactive && (
                        <div className="mt-2 ml-6 rounded-lg border border-red-200 bg-red-50 p-2 text-[11px] text-red-700">
                          <div className="font-semibold">Registro inativado</div>
                          {apt.inactive_at && (
                            <div>
                              Inativado em:{' '}
                              {new Date(apt.inactive_at).toLocaleString('pt-BR')}
                            </div>
                          )}
                          {apt.inactive_reason && (
                            <div>Motivo: {apt.inactive_reason}</div>
                          )}
                        </div>
                      )}

                      {apt.patient_acknowledgement_at && (
                        <div className="ml-6 text-[11px] text-gray-500 space-y-0.5">
                          <div>
                            Confirmado em:{' '}
                            {new Date(apt.patient_acknowledgement_at).toLocaleString('pt-BR')}
                          </div>
                          {apt.patient_acknowledgement_ip && (
                            <div>IP registrado: {apt.patient_acknowledgement_ip}</div>
                          )}
                          {apt.patient_acknowledgement_user_agent && (
                            <div className="break-all">
                              Dispositivo/navegador: {apt.patient_acknowledgement_user_agent}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {reviewingEvolutionId === aptId && !apt._isOdontogram && (
                <EvolutionAIReviewCard
                  patientId={patientId}
                  evolutionId={apt.id}
                  existingAnalysis={aiReviewsByEvolution[aptId]}
                  onAnalysisChange={(analysis) =>
                    setAiReviewsByEvolution((current) => ({
                      ...current,
                      [aptId]: analysis,
                    }))
                  }
                  onClose={() => setReviewingEvolutionId(null)}
                  showSuccess={notifySuccess}
                  showError={notifyError}
                />
              )}

              <div className="flex justify-end pt-3 gap-2 flex-wrap">
                {editingId === apt.id ? null : (
                  <>
                    {!apt._isOdontogram && !apt.is_inactive && (
                      <button
                        type="button"
                        onClick={() => setReviewingEvolutionId(aptId)}
                        className="px-3 py-1.5 text-purple-700 bg-purple-50 rounded-lg hover:bg-purple-100 flex items-center text-sm"
                        title="Verificar clareza e completude do registro"
                      >
                        <Sparkles className="w-4 h-4 mr-1" />
                        {aiReviewsByEvolution[aptId]?.status === 'stale'
                          ? 'Revisar novamente'
                          : aiReviewsByEvolution[aptId]?.status === 'accepted'
                          ? 'IA revisada'
                          : 'Revisar registro com IA'}
                      </button>
                    )}
                    {!apt._isOdontogram && (
                      <button
                        onClick={() => handleCopyAcknowledgementLink(apt)}
                        className="px-3 py-1.5 text-purple-700 bg-purple-50 rounded-lg hover:bg-purple-100 flex items-center text-sm"
                        disabled={loading || !apt.evolution_notes}
                        title="Gerar e copiar link para o paciente confirmar ciência desta evolução"
                      >
                        <LinkIcon className="w-4 h-4 mr-1" />
                        <Copy className="w-3 h-3 mr-1" />
                        Copiar link de ciência
                      </button>
                    )}

                    {!apt.is_inactive && (
                      <button
                        onClick={() => handleInactiveEvolution(apt)}
                        className="px-3 py-1.5 text-red-600 bg-red-50 rounded-lg hover:bg-red-100 flex items-center text-sm"
                        disabled={loading}
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        Inativar registro
                      </button>
                    )}
                    {!apt._isOdontogram && !apt.is_inactive && (
                      <button
                        onClick={() => startEdit(apt)}
                        className="px-3 py-1.5 text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 flex items-center text-sm"
                      >
                        <Edit3 className="w-4 h-4 mr-1" />
                        Editar Evolução
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <Activity className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h4 className="text-lg font-medium text-gray-900 mb-2">
            Nenhum histórico de atendimento encontrado
          </h4>
          <p className="text-gray-600 mb-4">
            O histórico é gerado quando você marca um agendamento como
            &quot;Realizado&quot; ou &quot;Não Compareceu&quot;, ou cria uma
            nova evolução manual.
          </p>
        </div>
      )}
    </div>
  );
}
